// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer, useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import FieldValue from '@shared/design/components/Field/FieldValue';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import Text from '@shared/design/components/Text';
import EditProjectAssignedStaffDrawer from '@shared/modules/Project/components/widgets/components/EditProjectAssignedStaffDrawer';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
  EditButton,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';

const Column = Styled.View`
`;

const SkeletonField = ({label}: {label: string}) => {
  const responsive = useResponsive();

  return (
    <Column style={{flex: 1}}>
      <Text.Label style={{color: colors.gray.secondary}}>{label}</Text.Label>
      <FieldValue.LabelSpace isResponsive responsive={responsive} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={200}
      />
    </Column>
  );
};

const SkeletonComponent = () => {
  return (
    <Column>
      <BlockHeaderLoader title={'Staff'} ActionsComponent={() => <EditButton isDisabled />} />
      <SkeletonField label={'Salesperson'} />
      <Space height={16} />
      <SkeletonField label={'Additional Salespersons'} />
      <Space height={16} />
      <SkeletonField label={'Coordinator'} />
      <Space height={16} />
      <SkeletonField label={'Created By'} />
      <Space height={16} />
    </Column>
  );
};

const StaffField = ({value, label}: {value: string; label: string}) => {
  return <FieldValue value={value} label={label} empty={'None'} isResponsive />;
};

const AssignedStaffFields = ({project}: {project: ProjectModel}) => {
  const {isEnabledAdditionalSalespersons} = project.organization.features;

  return (
    <React.Fragment>
      <StaffField label={'Salesperson'} value={project.bookedBy?.fullName} />
      <Space height={16} />
      {isEnabledAdditionalSalespersons ? (
        <React.Fragment>
          <StaffField
            label={
              project.organization.features.isEnabledProjectManagersField
                ? 'Project Managers'
                : 'Additional Salespersons'
            }
            value={project.additionalSalespersons
              .map((salesperson) => salesperson.fullName)
              .join(', ')}
          />
          <Space height={16} />
          <StaffField label={'Coordinator'} value={project.coordinatedBy?.fullName} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <StaffField label={'Coordinator'} value={project.coordinatedBy?.fullName} />
        </React.Fragment>
      )}
      <Space height={16} />
      <StaffField label={'Created By'} value={project.creator?.fullName} />
      <Space height={16} />
    </React.Fragment>
  );
};

const EditAssignedStaffButton = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  const editAssignedStaffDrawer = useDrawer({
    name: 'Edit Project Staff Drawer',
  });

  return (
    <React.Fragment>
      <EditButton onPress={editAssignedStaffDrawer.handleOpen} />
      <EditProjectAssignedStaffDrawer
        key={editAssignedStaffDrawer.key}
        project={project}
        isOpen={editAssignedStaffDrawer.isOpen}
        handleClose={editAssignedStaffDrawer.handleClose}
        refetch={refetch}
      />
    </React.Fragment>
  );
};

const ProjectDetailsAssignedStaffBlockContentMobile = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={`Staff`}
        ActionsComponent={EditAssignedStaffButton}
        actionsComponentProps={{project, refetch}}
      />
      <AssignedStaffFields project={project} />
    </React.Fragment>
  );
};

const ProjectDetailsAssignedStaffBlock = ({
  projectUuid,
  index,
}: {
  projectUuid: string;
  index: number;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      index={index}
      query={ProjectDetailsAssignedStaffBlock.query}
      queryVariables={{projectUuid}}
      SkeletonComponent={SkeletonComponent}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return (
          <ProjectDetailsAssignedStaffBlockContentMobile project={data.project} refetch={refetch} />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsAssignedStaffBlock.query = gql`
  ${EditProjectAssignedStaffDrawer.fragment}

  query ProjectDetailsAssignedStaffBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      bookedBy {
        id
        fullName
      }
      additionalSalespersons {
        id
        fullName
      }
      coordinatedBy {
        id
        fullName
      }
      creator {
        id
        fullName
      }
      organization {
        id
        features {
          isEnabledAdditionalSalespersons: isEnabled(feature: "ADDITIONAL_SALESPERSON_IDS_INPUT")
          isEnabledProjectCoordinatorRequired: isEnabled(feature: "PROJECT_COORDINATOR_REQUIRED")
          isEnabledProjectManagersField: isEnabled(feature: "PROJECT_MANAGERS_FIELD")
        }
      }
      ...EditProjectAssignedStaffDrawer
    }
  }
`;

export default ProjectDetailsAssignedStaffBlock;
